<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :perPage="itemsPerPage"
            :key="tableKey"
            :columns="tableColumns"
            :items="paginatedProducts"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="true"
            @drag-end="handleDragEnd"
            :enableMultiSelect="true"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-row ppv2-gap-2 ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect :options="exportOptions" :hide-label-on-mobile="true" @export-selected="exportData" />

                        <!-- Filter Button -->
                        <button
                            @click="showAdvancedFilters = true"
                            class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-text-sm ppv2-text-gray-700 hover:ppv2-bg-gray-50 ppv2-transition-colors"
                        >
                            <LayoutGrid :size="18" />
                            <span class="ppv2-hidden md:ppv2-inline">{{ $t('products.actions.filter') }}</span>
                        </button>
                    </div>

                    <!-- Create Button -->
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="navigateToCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('products.actions.add') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Name -->
            <template #column-name="{ item }">
                <a
                    @click.prevent="openEditTab(item.id)"
                    href="#"
                    class="ppv2-text-black poppinsRegular hover:ppv2-text-[#7D2DBD] ppv2-transition-colors ppv2-underline"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    {{ item.name }}
                </a>
            </template>

            <!-- Column: Brand -->
            <template #column-brand="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                    item.brand?.name || '-'
                }}</span>
            </template>

            <!-- Column: Visible Online (État) -->
            <template #column-visible_online="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.visible_online ? $t('products.status.visible') : $t('products.status.hidden') }}
                </span>
            </template>

            <!-- Column: Active (Statut) -->
            <template #column-active="{ item }">
                <button
                    @click="toggleActive(item)"
                    class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-1 ppv2-transition-all"
                    :class="
                        item.active === 1
                            ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                            : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                    "
                >
                    <div
                        class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                        :class="item.active === 1 ? 'ppv2-bg-[#206A64] ppv2-justify-end' : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'"
                    >
                        <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                    </div>
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ item.active === 1 ? $t('products.status.active') : $t('products.status.inactive') }}
                    </span>
                </button>
            </template>

            <!-- Column: Category -->
            <template #column-category="{ item }">
                <div
                    :style="{ fontSize: normalizeFontSize(14) }"
                    class="ppv2-text-black ppv2-inline-flex ppv2-px-3 ppv2-rounded-xl ppv2-text-base md:ppv2-text-lg ppv2-bg-opacity-30"
                >
                    {{ item.category?.name || '-' }}
                </div>
            </template>

            <!-- Column: Price -->
            <template #column-price="{ item }">
                <template v-if="item.discount_price && parseFloat(item.discount_price) > 0">
                    <span class="ppv2-text-black ppv2-line-through ppv2-opacity-50" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ formatPrice(item.price) }}
                    </span>
                    <span class="ppv2-text-[#911414] poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ formatPrice(item.discount_price) }}
                    </span>
                </template>
                <template v-else>
                    <span class="ppv2-text-black poppinsRegular">{{ formatPrice(item.price) }}</span>
                </template>
            </template>

            <!-- Column: Stock -->
            <template #column-stock="{ item }">
                <span v-if="item.quantity === null || item.quantity === undefined" :style="{ fontSize: normalizeFontSize(14) }">
                    &infin;
                </span>
                <div v-else>
                    <span
                        :style="{ fontSize: normalizeFontSize(14) }"
                        class="ppv2-text-black poppinsRegular"
                        :class="{
                            'ppv2-text-[#911414] poppinsRegular': item.quantity <= item.threshold_alert,
                        }"
                    >
                        {{ item.quantity }}
                    </span>
                    <AlertTriangle v-if="item.quantity <= item.threshold_alert" class="ppv2-text-[#911414] ppv2-w-4 ppv2-h-4" />
                </div>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div
                    :style="{ fontSize: normalizeFontSize(14) }"
                    class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                >
                    <button
                        @click="openEditTab(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('products.actions.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        @click="duplicated(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('products.actions.duplicate')"
                    >
                        <CopyPlus :size="18" />
                    </button>
                    <button
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('products.actions.delete')"
                        @click="deleteSingleItem(item.id)"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular ppv2-text-black">
                    {{ selectedCount }} élément{{ selectedCount > 1 ? 's' : '' }} sélectionné{{ selectedCount > 1 ? 's' : '' }}
                </span>
                <BaseButton
                    variant="outline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="14" />
                    <span>{{ $t('common.deleteSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('products.deletePopup.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('products.deletePopup.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Filter Popup -->
        <ProductAdvancedFiltersV3
            v-if="showAdvancedFilters"
            :available-filters="availableFilterOptions"
            :current-filters="activeFilters"
            @close="showAdvancedFilters = false"
            @update="handleAdvancedFiltersUpdate"
        />
    </div>
</template>

<script>
import { Plus, CopyPlus, Trash2, SquarePen, LayoutGrid, ChevronDown, AlertTriangle, X } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../Experience/components/Search.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import DataTable from '../../Shared/DataTable.vue'
import ProductAdvancedFiltersV3 from './ProductAdvancedFiltersV3.vue'
import { eventBus } from '../../Customer/eventBus'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    props: {
        userId: {
            type: [Number, String, null],
            required: false,
            default: null,
        },
        token: {
            type: String,
            default: '',
        },
    },
    name: 'ProductsTable',
    components: {
        Plus,
        SquarePen,
        CopyPlus,
        Trash2,
        Search,
        BaseButton,
        CustomDialog,
        ExportSelect,
        DataTable,
        ProductAdvancedFiltersV3,
        LayoutGrid,
        ChevronDown,
        AlertTriangle,
        X,
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('products_items_per_page', 10),
            nbreOfPages: 1,
            selectedItems: [],
            selectAll: false,
            isLoading: false,
            error: null,
            tableColumns: [
                { key: 'name', label: this.$t('products.table.name'), sortable: true, type: 'text' },
                { key: 'price', label: this.$t('products.table.price'), sortable: true, type: 'number' },
                { key: 'category', label: this.$t('products.table.category'), sortable: true, type: 'text' },
                { key: 'brand', label: this.$t('products.table.brand'), sortable: true, type: 'text' },
                { key: 'active', label: this.$t('products.table.status'), sortable: true, type: 'badge' },
                {
                    key: 'visible_online',
                    label: this.$t('products.table.visible'),
                    sortable: true,
                    align: 'center',
                },
                { key: 'stock', label: this.$t('products.table.stock'), sortable: true, type: 'number' },
                { key: 'actions', label: this.$t('products.table.actions'), sortable: false, type: 'actions' },
            ],
            showDeletePopup: false,
            deletePopupTitle: this.$t('products.deletePopup.title'),
            deletePopupDescription: '',
            deleteAction: null, // 'single' or 'multiple'
            itemToDelete: null,
            exportOptions: [
                { value: 'csv', label: this.$t('products.export.csv') },
                { value: 'xls', label: this.$t('products.export.xls') },
            ],
            products: {
                data: [],
                total: 0,
            },
            createUrl: '/back-office/v3/product/create',
            showFilterPopup: false,
            showAdvancedFilters: false,
            activeFilters: {
                categories: [],
                subcategories: [],
                brands: [],
                suppliers: [],
                price: {
                    greater_than: null,
                    lesser_than: null,
                },
                inventory: null,
                active: null,
                visible_online: null,
                is_cabin: null,
            },
            availableFilterOptions: {
                categories: [],
                subcategories: [],
                brands: [],
                suppliers: [],
            },
            isSavingPositions: false,
            tableKey: 0,
            orderBy: null,
            orderDirection: 'desc',
        }
    },

    computed: {
        activeFiltersCount() {
            if (!this.activeFilters) return 0
            let count = 0
            const f = this.activeFilters
            if (f.categories?.length) count++
            if (f.subcategories?.length) count++
            if (f.brands?.length) count++
            if (f.suppliers?.length) count++
            if (f.price?.greater_than !== null && f.price?.greater_than !== '') count++
            if (f.price?.lesser_than !== null && f.price?.lesser_than !== '') count++
            if (f.inventory) count++
            if (f.active !== null) count++
            if (f.visible_online !== null) count++
            if (f.is_cabin) count++
            return count
        },
        activeFiltersPills() {
            if (!this.activeFilters) return {}
            const pills = {}
            const f = this.activeFilters

            if (f.categories?.length) pills.categories = this.getFilterPillLabel('categories', f.categories)
            if (f.subcategories?.length) pills.subcategories = this.getFilterPillLabel('subcategories', f.subcategories)
            if (f.brands?.length) pills.brands = this.getFilterPillLabel('brands', f.brands)
            if (f.suppliers?.length) pills.suppliers = this.getFilterPillLabel('suppliers', f.suppliers)
            if (f.price?.greater_than !== null && f.price?.greater_than !== '')
                pills.price_min = this.getFilterPillLabel('price_min', f.price.greater_than)
            if (f.price?.lesser_than !== null && f.price?.lesser_than !== '')
                pills.price_max = this.getFilterPillLabel('price_max', f.price.lesser_than)
            if (f.inventory) pills.inventory = this.getFilterPillLabel('inventory', f.inventory)
            if (f.active !== null) pills.active = this.getFilterPillLabel('active', f.active)
            if (f.visible_online !== null) pills.visible_online = this.getFilterPillLabel('visible_online', f.visible_online)
            if (f.is_cabin) pills.is_cabin = this.getFilterPillLabel('is_cabin', f.is_cabin)

            return pills
        },
        filteredProducts() {
            const search = this.searchQuery?.toLowerCase().trim() || ''

            if (!this.products?.data) return []

            return this.products.data.filter((p) => {
                const name = p.name?.toLowerCase() || ''
                const matchesSearch = !search || name.includes(search)
                return matchesSearch
            })
        },
        paginatedProducts() {
            return this.filteredProducts || []
        },
        totalPages() {
            return this.products.total
        },
    },
    mounted() {
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        this.fetchProducts()
        this.fetchFilterOptions()
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        navigateToCreate() {
            window.location.href = this.createUrl
        },
        async toggleActive(item, newValue) {
            const previousActive = item.active
            const previousVisible = item.visible_online
            const newStatus = newValue !== undefined ? (newValue ? 1 : 0) : item.active === 1 ? 0 : 1
            item.active = newStatus

            if (newStatus === 0) {
                item.visible_online = 0
            } else {
                item.visible_online = 1
            }

            try {
                const response = await axios.patch(
                    '/api/v3/product/update-status',
                    {
                        id: item.id,
                        active: newStatus,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                )

                if (!response.data.success) {
                    item.active = previousActive
                    item.visible_online = previousVisible
                    alert('Erreur lors de la mise à jour du statut')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                item.active = previousActive
                item.visible_online = previousVisible
            }
        },
        async toggleVisibleOnline(item) {
            const previousActive = item.active
            const previousVisible = item.visible_online
            const newStatus = !previousVisible
            item.visible_online = newStatus

            if (newStatus) {
                item.active = 1
            }

            try {
                const response = await axios.patch(
                    '/api/v3/product/update-visible-online',
                    {
                        id: item.id,
                        visible_online: newStatus ? 1 : 0,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                )

                if (!response.data.success) {
                    item.active = previousActive
                    item.visible_online = previousVisible
                    alert('Erreur lors de la mise à jour de la visibilité')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                item.active = previousActive
                item.visible_online = previousVisible
                console.error('Visibility update error:', error)
                alert('Erreur lors de la mise à jour de la visibilité')
            }
        },
        formatPrice(price) {
            if (!price) return '-'
            return new Intl.NumberFormat(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(price)
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchProducts()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchProducts()
        },
        handleSort(key) {
            let sortKey = key
            if (key === 'category') sortKey = 'category_id'
            if (key === 'brand') sortKey = 'brand_id'
            if (key === 'stock') sortKey = 'quantity'

            if (this.orderBy === sortKey) {
                this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.orderBy = sortKey
                this.orderDirection = 'asc'
            }
            this.currentPage = 1
            this.fetchProducts()
        },

        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.paginatedProducts.length
        },

        toggleSelectAll() {
            const allIds = this.paginatedProducts.map((item) => item.id)
            const allSelected = allIds.length > 0 && allIds.every((id) => this.selectedItems.includes(id))
            if (allSelected) {
                this.selectedItems = []
            } else {
                this.selectedItems = [...allIds]
            }
        },

        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return

            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('products.deletePopup.message')
            this.showDeletePopup = true
        },

        deleteSingleItem(itemId) {
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            this.deletePopupDescription = this.$t('products.deletePopup.message')
            this.showDeletePopup = true
        },

        async confirmDelete() {
            if (this.deleteAction === 'multiple') {
                try {
                    await axios.delete('/api/v3/product/delete', {
                        data: {
                            ids: this.selectedItems,
                        },
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                            'Content-Type': 'application/json',
                        },
                    })

                    await this.fetchProducts()
                    this.selectedItems = []
                    this.selectAll = false
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Delete error:', error)
                    alert('Erreur lors de la suppression')
                }
            } else if (this.deleteAction === 'single' && this.itemToDelete) {
                try {
                    await axios.delete('/api/v3/product/delete', {
                        data: {
                            ids: [this.itemToDelete],
                        },
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                            'Content-Type': 'application/json',
                        },
                    })

                    await this.fetchProducts()
                    this.selectedItems = this.selectedItems.filter((id) => id !== this.itemToDelete)
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Delete error:', error)
                    alert('Erreur lors de la suppression')
                }
            }

            this.$emit('refresh-stats')
            this.showDeletePopup = false
            this.deleteAction = null
            this.itemToDelete = null
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('products_items_per_page', this.itemsPerPage)
            this.fetchProducts()
        },
        openEditTab(itemId) {
            window.location.href = `/back-office/v3/product/${itemId}/edit`
        },

        async fetchProducts() {
            this.isLoading = true
            this.error = null

            try {
                let url = `/api/v3/product/list?currentPage=${this.currentPage}&search=${this.searchQuery}&itemsPerPage=${this.itemsPerPage}&userId=${this.userId}`

                if (this.orderBy) {
                    url += `&orderBy=${this.orderBy}&orderDirection=${this.orderDirection}`
                }

                // Append filters to URL
                if (this.activeFilters !== null) {
                    url += `&filters=${encodeURIComponent(JSON.stringify(this.activeFilters))}`
                }

                const response = await axios.get(url, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                        'Content-Type': 'application/json',
                    },
                })
                const data = response.data

                this.products = {
                    data: data.data || [],
                    total: data.last_page || 0,
                }
                if (data.activeFilters) {
                    this.activeFilters = data.activeFilters
                } else if (this.activeFilters === null) {
                    this.activeFilters = {}
                }
            } catch (err) {
                this.error = err.message || 'Une erreur est survenue'
                console.error('API error:', err)
            } finally {
                this.isLoading = false
            }
        },
        duplicated(itemId) {
            this.duplicatedData(itemId)
        },
        async duplicatedData(id) {
            try {
                const response = await axios.post(
                    `/api/v3/product/duplicate`,
                    {
                        id: id,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                )

                this.$toast?.success?.(this.$t('products.table.duplicationSuccess'))
                await this.fetchProducts()
                this.$emit('refresh-stats')
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Duplication error:', error)
            }
        },

        async exportData(format) {
            try {
                let apiUrl = `/api/v3/product/export?format=${format}&page=${this.currentPage}&search=${this.searchQuery}&itemsPerPage=${this.itemsPerPage}&userId=${this.userId}`

                if (this.orderBy) {
                    apiUrl += `&orderBy=${this.orderBy}&orderDirection=${this.orderDirection}`
                }

                const response = await axios.get(apiUrl, {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                        'Content-Type': 'application/json',
                    },
                })

                const blob = new Blob([response.data])
                const url = window.URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = url

                const fileName = `export.${format}`
                link.setAttribute('download', fileName)

                document.body.appendChild(link)
                link.click()
                link.remove()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Export error:', error)
            }
        },
        handleAdvancedFiltersUpdate(filters) {
            this.activeFilters = filters
            this.currentPage = 1
            this.fetchProducts()
        },
        async fetchFilterOptions() {
            try {
                const response = await axios.get('/api/v3/product/filters-data')
                this.availableFilterOptions = response.data
            } catch (error) {
                console.error('Error fetching filter options:', error)
            }
        },
        getFilterPillLabel(key, value) {
            const labels = {
                categories: this.$t('products.filters.categories'),
                subcategories: this.$t('products.filters.subcategories'),
                brands: this.$t('products.filters.brands'),
                suppliers: this.$t('products.filters.suppliers'),
                price_min: this.$t('products.filters.minPrice'),
                price_max: this.$t('products.filters.maxPrice'),
                inventory: this.$t('products.filters.inventory'),
                active: this.$t('products.filters.status'),
                visible_online: this.$t('products.filters.visibleOnline'),
                is_cabin: this.$t('products.filters.isCabin'),
            }

            let displayValue = value

            if (Array.isArray(value)) {
                let names = []
                if (key === 'categories') {
                    names = value.map((id) => this.availableFilterOptions.categories.find((c) => c.id === id)?.name || id)
                } else if (key === 'subcategories') {
                    names = value.map((id) => this.availableFilterOptions.subcategories.find((c) => c.id === id)?.name || id)
                } else if (key === 'brands') {
                    names = value.map((id) => this.availableFilterOptions.brands.find((b) => b.id === id)?.name || id)
                } else if (key === 'suppliers') {
                    names = value.map((id) => this.availableFilterOptions.suppliers.find((s) => s.id === id)?.name || id)
                }
                displayValue = names.length > 2 ? `${names.length} sélectionné(s)` : names.join(', ')
            } else if (key === 'active') {
                displayValue = value === 1 ? this.$t('products.status.active') : this.$t('products.status.inactive')
            } else if (key === 'visible_online') {
                displayValue = value === 1 ? this.$t('products.status.visible') : this.$t('products.status.hidden')
            } else if (key === 'inventory') {
                const opt = [
                    { label: this.$t('products.filters.inventoryAlert'), value: 'alert' },
                    { label: this.$t('products.filters.inventoryOut'), value: 'out' },
                    { label: this.$t('products.filters.inventoryOrdering'), value: 'ordering' },
                ].find((o) => o.value === value)
                displayValue = opt ? opt.label : value
            } else if (key === 'is_cabin') {
                displayValue = this.$t('common.yes') || 'Oui'
            }

            return {
                label: labels[key] || key,
                value: displayValue,
            }
        },
        removeFilter(key) {
            const f = { ...this.activeFilters }
            if (key === 'categories' || key === 'subcategories' || key === 'brands' || key === 'suppliers') {
                f[key] = []
            } else if (key === 'price_min') {
                f.price.greater_than = null
            } else if (key === 'price_max') {
                f.price.lesser_than = null
            } else {
                f[key] = null
            }
            this.activeFilters = f
            this.handleAdvancedFiltersUpdate(f)
        },
        clearAllFilters() {
            this.activeFilters = {
                categories: [],
                subcategories: [],
                brands: [],
                suppliers: [],
                price: {
                    greater_than: null,
                    lesser_than: null,
                },
                inventory: null,
                active: null,
                visible_online: null,
                is_cabin: null,
            }
            this.handleAdvancedFiltersUpdate(this.activeFilters)
        },
        handleFormSuccess() {
            this.showFormPopup = false
            this.editingItem = null
            this.fetchProducts()
            this.$emit('refresh-stats')
        },
        async handleDragEnd(event) {
            const { oldIndex, newIndex } = event

            if (oldIndex === newIndex) return

            // Create a copy of the array to work with
            const currentItems = [...this.products.data]

            // Store original orders before any changes
            const originalOrders = currentItems.map((item) => ({
                id: item.id,
                order: item.order !== null && item.order > -1 ? item.order : item.id,
            }))

            // Calculate scores (order if set > -1, otherwise id)
            const scores = currentItems.map((item) => {
                return item.order !== null && item.order > -1 ? item.order : item.id
            })

            // Sort scores DESC (Highest first)
            scores.sort((a, b) => b - a)

            // Reorder the items array locally
            const movedItem = this.products.data.splice(oldIndex, 1)[0]
            this.products.data.splice(newIndex, 0, movedItem)

            // Assign the sorted scores to the items in their new order
            // Only collect positions that actually changed
            const changedPositions = []
            this.products.data.forEach((item, index) => {
                const newOrder = scores[index]
                const originalItem = originalOrders.find((o) => o.id === item.id)

                // Only include if the order actually changed
                if (originalItem && originalItem.order !== newOrder) {
                    changedPositions.push({
                        id: item.id,
                        order: newOrder,
                    })
                }

                item.order = newOrder // Update local model
            })

            // Only save if there are actual changes
            if (changedPositions.length > 0) {
                await this.savePositions(changedPositions)
            }
        },
        async savePositions(positions) {
            this.isSavingPositions = true

            try {
                const response = await axios.patch(
                    '/api/v3/product/update-positions',
                    {
                        positions: positions,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                )

                if (response.data.success) {
                    // Optionally refresh to ensure consistency
                    await this.fetchProducts()
                    this.tableKey++
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving positions:', error)
                alert("Erreur lors de la sauvegarde de l'ordre")
                // Revert on error
                await this.fetchProducts()
            } finally {
                this.isSavingPositions = false
            }
        },
    },
    watch: {
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.paginatedProducts.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },

        currentPage() {
            this.selectedItems = []
            this.selectAll = false
        },

        filteredProducts() {
            this.selectedItems = []
            this.selectAll = false
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
</style>
